import React from 'react';
import { BankOutlined, CheckCircleFilled } from '@ant-design/icons';

const MARK_PALETTE = ['#b3261e', '#2f6b1f', '#1f4e79', '#6b4f1f', '#3d348b'];

export const bankInitials = (bankName?: string | null): string => {
  const parts = (bankName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return 'BK';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export const bankLogoSrc = (logo?: string | null): string | undefined => {
  const value = logo?.trim();
  if (!value) {
    return undefined;
  }
  if (value.startsWith('data:') || value.startsWith('http') || value.startsWith('/')) {
    return value;
  }
  return `data:image/png;base64,${value}`;
};

const markColor = (name: string) => {
  const index = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0) % MARK_PALETTE.length;
  return MARK_PALETTE[index];
};

export const BankMark: React.FC<{ logo?: string | null; name: string }> = ({ logo, name }) => {
  const src = bankLogoSrc(logo);
  if (src) {
    return <img src={src} alt="" />;
  }
  return (
    <span className="linked-accounts-initials" style={{ background: markColor(name) }}>
      {bankInitials(name)}
    </span>
  );
};

export const LiabilityMark: React.FC = () => (
  <span className="linked-accounts-receipt" aria-hidden>
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6" />
      <path d="M9 12h6" />
      <path d="M9 16h3" />
    </svg>
  </span>
);

export const CashTypeIcon: React.FC<{ savings: boolean }> = ({ savings }) =>
  savings ? (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M19 5c-1.5 0-2.8 1.4-3 2-3.5-1.5-11-.3-11 5 0 1.8 0 3 2 4.5V20h4v-2h3v2h4v-4c1-.5 1.7-1 2-2h2v-4h-2c0-1-.5-1.5-1-2V5z" />
      <path d="M2 9v1c0 1.1.9 2 2 2h1" />
      <path d="M16 11h.01" />
    </svg>
  ) : (
    <BankOutlined aria-hidden />
  );

export const ConnectedStatus: React.FC = () => (
  <>
    <CheckCircleFilled aria-hidden /> Connected
  </>
);

export const NetWorthDelta: React.FC<{
  value: number;
  percentage: number;
  format: (value: number) => string;
}> = ({ value, percentage, format }) => (
  <span
    className={`delta ${value > 0 ? 'positive' : value < 0 ? 'negative' : 'zero'}`}
    style={{ marginLeft: '12px' }}
  >
    {value > 0 ? '+' : ''}
    {format(value)} ({percentage > 0 ? '+' : ''}
    {percentage.toFixed(2)}%)
  </span>
);

export type FundingChipStatus = 'connected' | 'pending' | 'not_set' | 'disconnected';

export type FundingChipAccountKind = 'checking' | 'savings' | 'other';

export type FundingChip = {
  accountId: string;
  status: FundingChipStatus;
  plaidId?: string;
  bankName?: string;
  bankLogo?: string | null;
  accountType?: string;
  last4?: string;
};

export const fundingBankLast4 = (value?: string | null): string | undefined => {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : undefined;
};

export const fundingChipAccountKind = (accountType?: string): FundingChipAccountKind => {
  const value = (accountType ?? '').trim().toLowerCase();
  if (value.includes('saving')) {
    return 'savings';
  }
  if (value.includes('checking')) {
    return 'checking';
  }
  return 'other';
};

export const fundingChipLabel = (chip: FundingChip): string => {
  if (chip.status === 'not_set') {
    return 'Add funding bank';
  }
  const typeLabel = chip.accountType?.trim();
  if (typeLabel && chip.last4) {
    return `${typeLabel} ••${chip.last4}`;
  }
  if (typeLabel) {
    return typeLabel;
  }
  if (chip.last4) {
    return `••${chip.last4}`;
  }
  return chip.bankName?.trim() || 'Linked bank';
};
